import React, { ReactNode, useMemo, useState } from 'react';
import { AdminsComponent, FormComponent, UiComponent } from 'components';
import { AdminsQuery } from 'queries';
import { useIsMobile, usePageOption } from 'hooks';
import useModal from 'hooks/useModal';
import { IAdmin, SearchAdminFilterType } from 'types';
import { tableToolbar, tableWrap } from 'styles/table';
import { useAppSelector } from 'stores';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';

const COLS = ['ID', '아이디', '이름', '이메일', '상태', '할당 권한', '관리'];

function List() {
  const isMobile = useIsMobile();

  const title = <UiComponent.PageTitle>어드민 계정</UiComponent.PageTitle>;

  if (isMobile) return <AdminsComponent.MobileList title={title} />;

  return <DesktopList title={title} />;
}

function DesktopList({ title }: { title: ReactNode }) {
  const pageState = usePageOption();
  const [filter, setFilter] = useState<SearchAdminFilterType>({});
  const { handleModal, handleConfirm } = useModal();
  const { mutateAsync: update } = AdminsQuery.useUpdate();
  const myId = useAppSelector((s) => s.auth.user?.id);
  const { data, isLoading } = AdminsQuery.useGetAll({
    page: pageState.pageOption.page,
    size: pageState.pageOption.size,
    ...filter,
  });

  const handleFilter = (next: SearchAdminFilterType) => {
    setFilter(next);
    pageState.handlePage({ ...pageState.pageOption, page: 1 });
  };

  const openForm = () =>
    handleModal('어드민 등록', <AdminsComponent.Form />, <></>);

  // 비활성 계정은 로그인·API 사용 즉시 차단
  const toggleActive = (admin: IAdmin) => {
    const next = !admin.isActive;
    handleConfirm({
      confirmContent: `'${admin.name || admin.username}' 계정을 ${next ? '활성화' : '비활성화'}하시겠습니까?`,
      confirmNext: () => {
        update({ id: admin.id, isActive: next })
          .then(() =>
            toast.success(next ? '활성화되었습니다' : '비활성화되었습니다'),
          )
          .catch(() => toast.error(DEFAULT_ERROR_MESSAGE));
      },
    });
  };

  const handleResetPassword = (admin: IAdmin) =>
    handleModal(
      `${admin.name || admin.username} · 비밀번호 재설정`,
      <AdminsComponent.PasswordReset admin={admin} />,
      <></>,
    );

  const openRoleChange = (admin: IAdmin) =>
    handleModal(
      `${admin.name || admin.username} · 역할 변경`,
      <AdminsComponent.RoleChange admin={admin} />,
      <></>,
    );

  const renderer = useMemo(
    () => ({
      ID: ({ id }: IAdmin) => <>{id}</>,
      아이디: ({ username }: IAdmin) => <>{username}</>,
      이름: ({ name }: IAdmin) => <>{name || '-'}</>,
      이메일: ({ email }: IAdmin) => <>{email || '-'}</>,
      상태: ({ isActive }: IAdmin) => <>{isActive ? '활성' : '비활성'}</>,
      '할당 권한': (admin: IAdmin) => (
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <span>
            {(admin.roles ?? []).map((r) => r.name).join(', ') || '-'}
          </span>
          {admin.id !== myId && (
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => openRoleChange(admin)}
            >
              변경
            </UiComponent.Button>
          )}
        </div>
      ),
      관리: (admin: IAdmin) =>
        admin.id === myId ? (
          <>-</>
        ) : (
          <div css={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => handleResetPassword(admin)}
            >
              비밀번호 재설정
            </UiComponent.Button>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => toggleActive(admin)}
            >
              {admin.isActive ? '비활성화' : '활성화'}
            </UiComponent.Button>
          </div>
        ),
    }),
    [myId],
  );

  const rows = (data?.data ?? []).map((a) => ({ ...a, rowKey: a.id }));

  return (
    <>
      {title}
      <UiComponent.Box>
        <div css={tableToolbar}>
          <FormComponent.Form
            submit={(d: SearchAdminFilterType) =>
              handleFilter({ name: d.name?.trim() || undefined })
            }
          >
            <FormComponent.SearchInput
              name="name"
              placeholder="이름으로 검색해주세요"
            />
          </FormComponent.Form>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={openForm}
          >
            어드민 등록
          </UiComponent.Button>
        </div>
        <div css={tableWrap}>
          <UiComponent.Table
            cols={COLS}
            renderer={renderer}
            data={rows}
            sticky
            empty="등록된 어드민이 없습니다"
          />
        </div>
        {!isLoading && (
          <UiComponent.Pagination {...pageState} totalPage={data?.totalPages} />
        )}
      </UiComponent.Box>
    </>
  );
}

export default List;
